import {
  Avatar,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { Fade, InformationModal } from 'utoniq-ui'

import {
  CommentsConnectionFragment,
  CommunityCommentInput,
  Maybe,
  TradeBoxFragment,
  TradeNegotiation,
  TradeNegotiationStatus,
  User,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  ReplyForm,
  ReplyFormValues,
  TradeDetailBox,
  TradeDetailBoxSkeleton,
  TradeMessageBox,
  TradeMessageBoxSkeleton,
} from '@/components/organisms'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { NotFound } from '../..'
import { TradeAcceptConfirmModal } from './TradeAcceptConfirmModal'
import { TradeCancelConfirmModal } from './TradeCancelConfirmModal'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  hasNextPage: boolean
  user: Maybe<Pick<User, 'id' | 'displayName' | 'avatar'>>
  trade: Maybe<Pick<TradeNegotiation, 'formattedExpiredAt'> & TradeBoxFragment>
  acceptTradeRequesting: boolean
  declineTradeRequesting: boolean
  cancelTradeRequesting: boolean
  messages: Maybe<CommentsConnectionFragment>
}

type TextCellProps = {
  trade: State['trade']
}

type FixedButtonCellProps = {
  loading: State['loading']
  isMyRequest: boolean
  acceptTradeRequesting: State['acceptTradeRequesting']
  declineTradeRequesting: State['declineTradeRequesting']
  cancelTradeRequesting: State['cancelTradeRequesting']
  onAcceptTradeRequest: (comment: CommunityCommentInput) => Promise<void>
  onDeclineTradeRequest: (comment: CommunityCommentInput) => Promise<void>
  onCancelTradeRequest: () => Promise<void>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onReply: (comment: CommunityCommentInput) => Promise<void>
  onAcceptTradeRequest: (
    tradeCandidateId: string,
    acceptComment: CommunityCommentInput
  ) => Promise<void>
  onDeclineTradeRequest: (
    tradeCandidateId: string,
    declineComment: CommunityCommentInput
  ) => Promise<void>
  onCancelTradeRequest: (tradeCandidateId: string) => Promise<void>
}

export const MyTradeDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onReply,
  onDeclineTradeRequest,
  onCancelTradeRequest,
  onAcceptTradeRequest,
}) => {
  const { routerNames, generatePath, routerPages } = useRouter()
  const { t } = useTranslation('myTradeDetail')
  const { showError } = useMessage()
  const [openReply, setOpenReply] = useState<boolean>(false)

  const isMyRequest = useMemo<boolean>(
    () => state.user?.id === state.trade?.request.requestedByUser?.id,
    [state.user?.id, state.trade?.request.requestedByUser?.id]
  )

  const artistId =
    state.trade?.candidates[0].candidateItems[0].item.issuedArtist?.id

  const title = useMemo<string>(() => {
    if (!state.trade?.status) {
      return t('title')
    }
    switch (state.trade?.status) {
      case TradeNegotiationStatus.InProgress:
        return isMyRequest
          ? t('inProgressSendTitle')
          : t('inProgressReceiveTitle')
      default:
        return t(`${state.trade?.status}Title`)
    }
  }, [state.trade?.status, t, isMyRequest])

  const handleReply = useCallback(
    async (values: ReplyFormValues) => {
      await onReply({
        artistId:
          state.trade?.candidates[0].candidateItems[0].item.issuedArtist?.id ??
          '',
        content: values.content,
        resources: values.resources,
      })
        .then(() => setOpenReply(false))
        .catch(showError)
    },
    [onReply, showError, state.trade?.candidates]
  )

  const handleAcceptTradeRequest = useCallback(
    async (comment: CommunityCommentInput) => {
      await onAcceptTradeRequest(state.trade?.candidates[0]?.id ?? '', {
        ...comment,
        artistId: state.user?.id ?? '',
      }).catch(showError)
    },
    [onAcceptTradeRequest, showError, state.trade?.candidates, state.user?.id]
  )

  const handleDeclineTradeRequest = useCallback(
    async (comment: CommunityCommentInput) => {
      await onDeclineTradeRequest(state.trade?.candidates[0]?.id ?? '', {
        ...comment,
        artistId: state.user?.id ?? '',
      }).catch(showError)
    },
    [onDeclineTradeRequest, showError, state.trade?.candidates, state.user?.id]
  )

  const handleCancelTradeRequest = useCallback(async () => {
    await onCancelTradeRequest(state.trade?.candidates[0]?.id ?? '').catch(
      showError
    )
  }, [onCancelTradeRequest, showError, state.trade?.candidates])

  /**
   * NFT 停止に向けて、NFTアイテムにリクエストされたトレード詳細ページを表示しない
   * NFT 機能停止後には削除して問題ない
   */
  if (state.trade?.candidates[0]?.candidateItems[0]?.item?.nft?.chains) {
    return <NotFound useNextRouter={false} />
  }

  return (
    <>
      <Head title={[routerNames.MyTradeDetail]} />
      <BaseLayout
        customNavigation={
          state.trade?.status === TradeNegotiationStatus.InProgress ? (
            <TradeBottomCell
              openReply={openReply}
              setOpenReply={setOpenReply}
              loading={state.loading}
              onReply={handleReply}
              userId={state.user?.id ?? ''}
              acceptTradeRequesting={state.acceptTradeRequesting}
              declineTradeRequesting={state.declineTradeRequesting}
              cancelTradeRequesting={state.cancelTradeRequesting}
              isMyRequest={isMyRequest}
              onAcceptTradeRequest={handleAcceptTradeRequest}
              onDeclineTradeRequest={handleDeclineTradeRequest}
              onCancelTradeRequest={handleCancelTradeRequest}
            />
          ) : undefined
        }
        header={{
          back: true,
          type: 'modal',
          title,
          defaultBackPage: artistId
            ? config.applicationType === 'standalone'
              ? generatePath(routerPages.ArtistTop, {
                  artistId,
                })
              : generatePath(routerPages.ArtistTrade, {
                  artistId,
                })
            : undefined,
        }}
      >
        {state.trade?.status === TradeNegotiationStatus.InProgress && (
          <TextCell trade={state.trade} />
        )}
        {!state.loading && state.trade ? (
          <TradeDetailBox trade={state.trade} myUserId={state.user?.id ?? ''} />
        ) : (
          <TradeDetailBoxSkeleton />
        )}
        <ScrollFetcher
          nodes={state.messages?.edges.map(({ node }, index) => (
            <TradeMessageBox message={node} key={index} />
          ))}
          loading={state.fetching || state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          loadingComponent={
            <Box sx={{ px: 5 }}>
              <TradeMessageBoxSkeleton />
            </Box>
          }
        />
        {state.loading && (
          <Box sx={{ px: 5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ py: 3 }}
            >
              <Stack>
                <Skeleton variant="circular" width={28} height={28} />
              </Stack>
              <Typography variant="body2" color="text.disabled">
                {t('sendMessage')}
              </Typography>
            </Stack>
            <Skeleton variant="rectangular" width="100%" height={1} />
          </Box>
        )}
        {state.trade?.status === TradeNegotiationStatus.InProgress && (
          <>
            <Box sx={{ px: 5 }}>
              <ListItemButton
                component={Box}
                sx={{ px: 0, py: 3 }}
                onClick={() => setOpenReply(openReply => !openReply)}
              >
                <ListItemAvatar sx={{ minWidth: 'auto', pr: 2 }}>
                  <Avatar
                    src={state.user?.avatar?.smallUri ?? ''}
                    sx={{ width: 28, height: 28 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={t('sendMessage')}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      color: 'text.disabled',
                    },
                  }}
                />
              </ListItemButton>
              <Divider />
            </Box>
          </>
        )}
      </BaseLayout>
    </>
  )
}

const TextCell: Cell<TextCellProps> = ({ trade }) => {
  const { t } = useTranslation('myTradeDetail')
  return (
    <CellFrame sx={{ py: 2, bgcolor: 'grey.50' }}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="caption">{t('tradeAlert')}</Typography>
        <Typography variant="overline" color="text.secondary">
          {t('appDeadline', { expiredAt: trade?.formattedExpiredAt })}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

// トレードを承認したりキャンセルしたりするボタン
const ProceedTradeButtonCell: Cell<FixedButtonCellProps> = ({
  loading,
  isMyRequest,
  acceptTradeRequesting,
  declineTradeRequesting,
  cancelTradeRequesting,
  onAcceptTradeRequest,
  onDeclineTradeRequest,
  onCancelTradeRequest,
}) => {
  const { t } = useTranslation('myTradeDetail')
  const [openAccept, setOpenAccept] = useState<boolean>(false)
  const [openDecline, setOpenDecline] = useState<boolean>(false)
  const [openCancel, setOpenCancel] = useState<boolean>(false)

  const handleTradeCancel = useCallback(async () => {
    await onCancelTradeRequest()
    setOpenCancel(false)
  }, [onCancelTradeRequest])

  return (
    <>
      <CellFrame sx={{ py: 4, textAlign: 'center' }}>
        {isMyRequest ? (
          <Button
            onClick={() => setOpenCancel(true)}
            size="large"
            color="primary"
            variant="outlined"
            sx={{ minWidth: 244 }}
            loading={loading}
          >
            {t('cancelApp')}
          </Button>
        ) : (
          <Stack spacing={5} direction="row" justifyContent="center">
            <Button
              onClick={() => setOpenDecline(true)}
              size="large"
              variant="outlined"
              sx={{ minWidth: 140 }}
            >
              {t('refuseTheTrade')}
            </Button>
            <Button
              onClick={() => setOpenAccept(true)}
              size="large"
              color="primary"
              sx={{ minWidth: 140 }}
              loading={acceptTradeRequesting}
              disabled={declineTradeRequesting}
            >
              {t('trade')}
            </Button>
          </Stack>
        )}
      </CellFrame>
      <FullscreenModal open={openAccept} onClose={() => setOpenAccept(false)}>
        <Fade style={{ height: '100%' }}>
          <TradeAcceptConfirmModal
            onSubmit={onAcceptTradeRequest}
            onClose={() => setOpenAccept(false)}
          />
        </Fade>
      </FullscreenModal>
      <FullscreenModal open={openDecline} onClose={() => setOpenDecline(false)}>
        <Fade style={{ height: '100%' }}>
          <TradeCancelConfirmModal
            onSubmit={onDeclineTradeRequest}
            onClose={() => setOpenDecline(false)}
          />
        </Fade>
      </FullscreenModal>
      <InformationModal
        open={openCancel}
        onClose={() => setOpenCancel(false)}
        title={t('tradeCancelTitle')}
        content={t('tradeCancelDesc')}
        cancelText={t('cancel')}
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            size="large"
            loading={cancelTradeRequesting}
            onClick={handleTradeCancel}
          >
            {t('cancelApp')}
          </Button>
        }
      />
    </>
  )
}

const TradeBottomCell = ({
  openReply,
  setOpenReply,
  onReply,
  userId,
  loading,
  acceptTradeRequesting,
  declineTradeRequesting,
  cancelTradeRequesting,
  isMyRequest,
  onAcceptTradeRequest,
  onDeclineTradeRequest,
  onCancelTradeRequest,
}: {
  openReply: boolean
  setOpenReply: (open: boolean) => void
  onReply: (values: ReplyFormValues) => Promise<void>
  userId: string
  loading: boolean
  acceptTradeRequesting: boolean
  declineTradeRequesting: boolean
  cancelTradeRequesting: boolean
  isMyRequest: boolean
  onAcceptTradeRequest: (comment: CommunityCommentInput) => Promise<void>
  onDeclineTradeRequest: (comment: CommunityCommentInput) => Promise<void>
  onCancelTradeRequest: () => Promise<void>
}) => {
  return openReply ? (
    <ClickAwayListener
      onClickAway={() => {
        setOpenReply(false)
      }}
    >
      <Box>
        <ReplyForm onReply={onReply} userId={userId} autoFocus />
      </Box>
    </ClickAwayListener>
  ) : (
    <ProceedTradeButtonCell
      loading={loading}
      acceptTradeRequesting={acceptTradeRequesting}
      declineTradeRequesting={declineTradeRequesting}
      cancelTradeRequesting={cancelTradeRequesting}
      isMyRequest={isMyRequest}
      onAcceptTradeRequest={onAcceptTradeRequest}
      onDeclineTradeRequest={onDeclineTradeRequest}
      onCancelTradeRequest={onCancelTradeRequest}
    />
  )
}
